// src/utils/uploadUtils.ts
// File upload utilities for web: presigned URL upload → register metadata
// Mirrors the mobile uploadUtils flow using the same backend endpoints.

const API_BASE = '/api';

export interface UploadedAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;
}

/**
 * Upload a single File object:
 *   1. GET /api/files/upload-url  → presigned URL + storageKey
 *   2. PUT <presignedUrl>         → upload raw file to storage
 *   3. POST /api/files            → register metadata, get asset id
 *
 * Returns an UploadedAttachment ready for inclusion in sendMessage.
 * Throws on any step failure.
 */
/**
 * Optional extra metadata merged into the register (POST /api/files) step —
 * e.g. { taskId, isProof } to attach the file to a task as a proof/attachment.
 */
export interface UploadExtraMeta {
  taskId?: string;
  isProof?: boolean;
  contactId?: string;
  projectId?: string;
  description?: string;
  /**
   * Store the file under a different name than the one on disk. The Files
   * page offers an editable File Name, so the register step must be able to
   * take it; `extra` is spread last into the body, so this wins.
   */
  originalName?: string;
}

export async function uploadFile(
  file: File,
  token: string,
  extra?: UploadExtraMeta,
): Promise<UploadedAttachment> {
  // Step 1: Get presigned URL
  const params = new URLSearchParams({
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
  });

  const presignedRes = await fetch(`${API_BASE}/files/upload-url?${params}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!presignedRes.ok) {
    const err = await presignedRes.json().catch(() => ({ error: 'Failed to get upload URL' }));
    throw new Error(err.error || 'Failed to get upload URL');
  }

  const { url, storageKey } = await presignedRes.json();

  // Step 2: Upload file to presigned URL
  const uploadRes = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });

  if (!uploadRes.ok) {
    throw new Error(`Upload failed with status ${uploadRes.status}`);
  }

  // Step 3: Register file metadata
  const registerRes = await fetch(`${API_BASE}/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      originalName: file.name,
      storageKey,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      storageProvider: 's3',
      ...extra,
    }),
  });

  if (!registerRes.ok) {
    const err = await registerRes.json().catch(() => ({ error: 'Failed to register file' }));
    throw new Error(err.error || 'Failed to register file');
  }

  const registered = await registerRes.json();

  return {
    id: registered.id,
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    url: registered.publicUrl || undefined,
  };
}

/**
 * Upload multiple files concurrently.
 * Returns successfully uploaded attachments; logs warnings for failures.
 * Never throws — partial success is allowed.
 */
export async function uploadFiles(
  files: File[],
  token: string,
  extra?: UploadExtraMeta,
): Promise<UploadedAttachment[]> {
  const results = await Promise.allSettled(files.map((f) => uploadFile(f, token, extra)));

  const attachments: UploadedAttachment[] = [];
  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      attachments.push(result.value);
    } else {
      console.warn(`[Upload] Failed for ${files[i].name}:`, result.reason);
    }
  });

  return attachments;
}
